import { isEqual, keyBy } from 'lodash';
import { useMemo } from 'react';

import { Input, ModelInference, ResultNameOrchestrations, SlideInferenceResults } from 'interfaces/calculateFeatures';
import { ClassificationModel } from 'interfaces/classificationModel';
import { CalculateFeaturesParams } from 'interfaces/job';
import { CellRules, Thresholds } from 'interfaces/jobs/multiplex/cellTypingParams';
import { AssignmentByStain, SelectedAssignmentDetails } from 'utils/assignmentManagement';
import { ModelPreferencesById } from 'utils/models';
import { SlideRegistrationDetailsByOrchestration } from 'utils/slideRegistrations';
import { getArtifactOrchestrationId } from './calculateFeaturesInputs';
import { buildPreviousJobComparisonStatus, PreviousJobComparisonStatus } from './previousJobComparisonStatus';
import {
  buildPreviousJobGeneralInputsSnapshot,
  buildPreviousJobGeneralInputsSnapshotFromPipsStudyInputs,
  buildPreviousJobSelectedResultsSnapshot,
  buildPreviousJobSelectedResultsSnapshotFromInputs,
  buildSelectedSlideRegistrationsByOrchestration,
  emptyPreviousJobGeneralInputsSnapshot,
  emptyPreviousJobSelectedResultsSnapshot,
  PreviousJobSelectedResultsSnapshot,
} from './previousJobSelectionSnapshots';

interface UsePreviousJobSelectionStatusParams {
  hasLoadedPreviousJobSelections: boolean;
  jobParams?: CalculateFeaturesParams;
  slides: SlideInferenceResults[];
  inferenceArtifacts: ModelInference[];
  postprocessedArtifacts?: ResultNameOrchestrations[];
  pipsResultsArtifacts?: ResultNameOrchestrations[];
  pipsAreaInstancesArtifacts?: ResultNameOrchestrations[];
  cellQuantificationArtifacts?: ResultNameOrchestrations[];
  areaQuantificationArtifacts?: ResultNameOrchestrations[];
  slideSegmentationsTableArtifacts?: ResultNameOrchestrations[];
  slideDetectionsTableArtifacts?: ResultNameOrchestrations[];
  classificationModelOptions: ClassificationModel[];
  getNewSelectedAssignmentDetails: (
    selectedAssignmentDetails: AssignmentByStain,
    stainingType: string,
    result: Input
  ) => SelectedAssignmentDetails[];
  selectedModelOrchestrations: PreviousJobSelectedResultsSnapshot['selectedModelOrchestrations'];
  selectedPostprocessedOrchestrations: PreviousJobSelectedResultsSnapshot['selectedPostprocessedOrchestrations'];
  selectedPipsOrchestrations: PreviousJobSelectedResultsSnapshot['selectedPipsOrchestrations'];
  selectedPipsAreaInstancesOrchestrations: PreviousJobSelectedResultsSnapshot['selectedPipsAreaInstancesOrchestrations'];
  selectedCellQuantificationOrchestrations: PreviousJobSelectedResultsSnapshot['selectedCellQuantificationOrchestrations'];
  selectedAreaQuantificationOrchestrations: PreviousJobSelectedResultsSnapshot['selectedAreaQuantificationOrchestrations'];
  selectedSlideSegmentationsTableOrchestrations: PreviousJobSelectedResultsSnapshot['selectedSlideSegmentationsTableOrchestrations'];
  selectedSlideDetectionsTableOrchestrations: PreviousJobSelectedResultsSnapshot['selectedSlideDetectionsTableOrchestrations'];
  selectedAssignments: PreviousJobSelectedResultsSnapshot['selectedAssignments'];
  modelPreferencesByModelId: ModelPreferencesById;
  selectedSlideRegistrations?: SlideRegistrationDetailsByOrchestration;
  perMarkerThresholds?: Thresholds | null;
  perMarkerThresholdsOrchestrationId?: string | null;
  cellRules?: CellRules | null;
  knnArtifactUrl?: string | null;
  restoredNeighborhoodClustersJobOrchestrationId?: string | null;
}

export const usePreviousJobSelectionStatus = ({
  hasLoadedPreviousJobSelections,
  jobParams,
  slides,
  inferenceArtifacts,
  postprocessedArtifacts,
  pipsResultsArtifacts,
  pipsAreaInstancesArtifacts,
  cellQuantificationArtifacts,
  areaQuantificationArtifacts,
  slideSegmentationsTableArtifacts,
  slideDetectionsTableArtifacts,
  classificationModelOptions,
  getNewSelectedAssignmentDetails,
  selectedModelOrchestrations,
  selectedPostprocessedOrchestrations,
  selectedPipsOrchestrations,
  selectedPipsAreaInstancesOrchestrations,
  selectedCellQuantificationOrchestrations,
  selectedAreaQuantificationOrchestrations,
  selectedSlideSegmentationsTableOrchestrations,
  selectedSlideDetectionsTableOrchestrations,
  selectedAssignments,
  modelPreferencesByModelId,
  selectedSlideRegistrations,
  perMarkerThresholds,
  perMarkerThresholdsOrchestrationId,
  cellRules,
  knnArtifactUrl,
  restoredNeighborhoodClustersJobOrchestrationId,
}: UsePreviousJobSelectionStatusParams): PreviousJobComparisonStatus => {
  const currentSelectedResultsSnapshot = useMemo(
    () =>
      buildPreviousJobSelectedResultsSnapshot({
        selectedModelOrchestrations,
        selectedPostprocessedOrchestrations,
        selectedPipsOrchestrations,
        selectedPipsAreaInstancesOrchestrations,
        selectedCellQuantificationOrchestrations,
        selectedAreaQuantificationOrchestrations,
        selectedSlideSegmentationsTableOrchestrations,
        selectedSlideDetectionsTableOrchestrations,
        selectedAssignments,
        modelPreferencesByModelId,
      }),
    [
      selectedModelOrchestrations,
      selectedPostprocessedOrchestrations,
      selectedPipsOrchestrations,
      selectedPipsAreaInstancesOrchestrations,
      selectedCellQuantificationOrchestrations,
      selectedAreaQuantificationOrchestrations,
      selectedSlideSegmentationsTableOrchestrations,
      selectedSlideDetectionsTableOrchestrations,
      selectedAssignments,
      modelPreferencesByModelId,
    ]
  );

  const currentSelectedSlideRegistrationsSnapshot = useMemo(
    () => selectedSlideRegistrations ?? {},
    [selectedSlideRegistrations]
  );

  const currentGeneralInputsSnapshot = useMemo(
    () =>
      buildPreviousJobGeneralInputsSnapshot({
        perMarkerThresholds: perMarkerThresholds ?? null,
        perMarkerThresholdsOrchestrationId: perMarkerThresholdsOrchestrationId ?? null,
        cellRules: cellRules ?? null,
        knnOrchestrationId: getArtifactOrchestrationId(knnArtifactUrl) ?? null,
        neighborhoodClustersOrchestrationId: restoredNeighborhoodClustersJobOrchestrationId ?? null,
      }),
    [
      perMarkerThresholds,
      perMarkerThresholdsOrchestrationId,
      cellRules,
      knnArtifactUrl,
      restoredNeighborhoodClustersJobOrchestrationId,
    ]
  );

  const previousJobSelectedResultsSnapshot = useMemo(() => {
    if (!hasLoadedPreviousJobSelections) {
      return emptyPreviousJobSelectedResultsSnapshot;
    }

    return buildPreviousJobSelectedResultsSnapshotFromInputs({
      params: jobParams ?? {},
      slidesById: keyBy(slides, 'slideId'),
      allInferenceResults: inferenceArtifacts,
      postprocessedArtifacts,
      pipsResultsArtifacts,
      pipsAreaInstancesArtifacts,
      cellQuantificationArtifacts,
      areaQuantificationArtifacts,
      slideSegmentationsTableArtifacts,
      slideDetectionsTableArtifacts,
      classificationModelOptions,
      getNewSelectedAssignmentDetails,
    });
  }, [
    hasLoadedPreviousJobSelections,
    jobParams,
    slides,
    inferenceArtifacts,
    postprocessedArtifacts,
    pipsResultsArtifacts,
    pipsAreaInstancesArtifacts,
    cellQuantificationArtifacts,
    areaQuantificationArtifacts,
    slideSegmentationsTableArtifacts,
    slideDetectionsTableArtifacts,
    classificationModelOptions,
    getNewSelectedAssignmentDetails,
  ]);

  const previousJobGeneralInputsSnapshot = useMemo(() => {
    if (!hasLoadedPreviousJobSelections) {
      return emptyPreviousJobGeneralInputsSnapshot;
    }

    return buildPreviousJobGeneralInputsSnapshotFromPipsStudyInputs(jobParams?.pipsStudyInputs);
  }, [hasLoadedPreviousJobSelections, jobParams]);

  const previousJobSelectedSlideRegistrationsSnapshot = useMemo(() => {
    if (!hasLoadedPreviousJobSelections) {
      return {};
    }

    return buildSelectedSlideRegistrationsByOrchestration(jobParams?.registrations);
  }, [hasLoadedPreviousJobSelections, jobParams]);

  return useMemo(() => {
    return buildPreviousJobComparisonStatus({
      hasPreviousJobSettings: hasLoadedPreviousJobSelections,
      comparisons: [
        {
          label: 'selected results',
          matches: isEqual(previousJobSelectedResultsSnapshot, currentSelectedResultsSnapshot),
        },
        {
          label: 'general inputs',
          matches: isEqual(previousJobGeneralInputsSnapshot, currentGeneralInputsSnapshot),
        },
        {
          label: 'slide registrations',
          matches: isEqual(previousJobSelectedSlideRegistrationsSnapshot, currentSelectedSlideRegistrationsSnapshot),
        },
      ],
    });
  }, [
    hasLoadedPreviousJobSelections,
    previousJobSelectedResultsSnapshot,
    currentSelectedResultsSnapshot,
    previousJobGeneralInputsSnapshot,
    currentGeneralInputsSnapshot,
    previousJobSelectedSlideRegistrationsSnapshot,
    currentSelectedSlideRegistrationsSnapshot,
  ]);
};
